import { createContext, useContext, useEffect, useState } from 'react';
import type { FC, ReactNode } from 'react';
import PropTypes from 'prop-types';

export interface Camera {
  id: string;
  title: string;
  url: string;
}

/**
 * Per-camera size calibration. The camera is fixed, so a known-size object gives a constant scale.
 * Stored as cm of real size per unit of frame-fraction (box side / frame side) per axis, which is
 * resolution-independent (works whether the box was measured on the live stream or a full snap).
 */
export interface CameraCalibration {
  /** Real cm per (object width px / frame width px). */
  cmPerFrameW: number;
  /** Real cm per (object height px / frame height px). */
  cmPerFrameH: number;
  /** The reference object's real size (cm) used to calibrate, kept for display/re-edit. */
  refWidthCm: number;
  refHeightCm: number;
}

/** A saved manual crop box in the stream's native pixel coords (fixed camera → stable). */
export interface CropBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface Settings {
  direction?: 'ltr' | 'rtl';
  language: 'en' | 'des' | 'es';
  pinSidebar: boolean;
  theme?: string;
  cameras?: Camera[];
  defaultCaptureMethod?: {
    protection?: string;
    verification?: string;
  };
  /** Per-camera capture rotation in degrees (0/90/180/270), keyed by camera id. */
  cameraRotation?: Record<string, number>;
  /** Per-camera manual-crop mode (drag the crop box by hand vs auto-detect), keyed by camera id. */
  manualCropEnabled?: Record<string, boolean>;
  /** Per-camera size calibration for auto-measuring an object's real size, keyed by camera id. */
  cameraCalibration?: Record<string, CameraCalibration>;
  /** Per-camera saved manual crop box, so the box position persists across toggles/sessions. */
  cameraCropBox?: Record<string, CropBox>;
}

export interface SettingsContextValue {
  settings: Settings;
  saveSettings: (update: Settings) => void;
}

interface SettingsProviderProps {
  children?: ReactNode;
}

const initialSettings: Settings = {
  direction: 'ltr',
  language: 'en',
  pinSidebar: true,
  theme: 'light',
  cameras: [],
  defaultCaptureMethod: {},
};

export const restoreSettings = (): Settings | null => {
  let settings = null;

  try {
    const storedData = window.localStorage.getItem('settings');

    if (storedData) {
      settings = JSON.parse(storedData);
    } else {
      settings = {
        direction: 'ltr',
        language: 'en',
        pinSidebar: true,
        // theme: window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light',
        theme: 'light',
        cameras: [],
        defaultCaptureMethod: {},
      };
    }
  } catch (err) {
    console.error(err);
    // If stored data is not a strigified JSON this will fail,
    // that's why we catch the error
  }

  if (
    !settings.cameras?.find((camera) => camera.id === settings.defaultCaptureMethod?.protection)
  ) {
    settings.defaultCaptureMethod = {
      ...settings.defaultCaptureMethod,
      protection: undefined,
    };
  }

  if (
    !settings.cameras?.find((camera) => camera.id === settings.defaultCaptureMethod?.verification)
  ) {
    settings.defaultCaptureMethod = {
      ...settings.defaultCaptureMethod,
      verification: undefined,
    };
  }

  return settings;
};

export const storeSettings = (settings: Settings): void => {
  window.localStorage.setItem('settings', JSON.stringify(settings));
};

export const SettingsContext = createContext<SettingsContextValue>({
  settings: initialSettings,
  saveSettings: () => {},
});

export const SettingsProvider: FC<SettingsProviderProps> = (props) => {
  const { children } = props;
  const [settings, setSettings] = useState<Settings>(initialSettings);

  useEffect(() => {
    const restoredSettings = restoreSettings();

    if (restoredSettings) {
      setSettings(restoredSettings);
    }
  }, []);

  const saveSettings = (updatedSettings: Settings): void => {
    setSettings(updatedSettings);
    storeSettings(updatedSettings);
  };

  return (
    <SettingsContext.Provider
      value={{
        settings,
        saveSettings,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

SettingsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const SettingsConsumer = SettingsContext.Consumer;

export const useSettings = () => useContext(SettingsContext);
